<template>
  <div class="jobs-top-bar">
    <div class="jobs-top-bar__row">
      <v-text-field
        :value="search"
        outlined
        dense
        clearable
        hide-details
        placeholder="Search scan, AVA Inspect or job id"
        aria-label="Search analysis jobs"
        prepend-inner-icon="mdi-magnify"
        class="jobs-top-bar__search"
        @input="$emit('update:search', $event || '')" />

      <div class="jobs-top-bar__filters" role="group" aria-label="Filter by status">
        <button
          v-for="status in statuses"
          :key="status"
          type="button"
          class="chip-filter"
          :data-status="status"
          :aria-pressed="String(statusFilter.includes(status))"
          @click="toggleStatus(status)">
          <span class="chip-filter__dot"></span>
          {{ status }}
          <span class="chip-filter__count">{{ statusCounts[status] || 0 }}</span>
        </button>
      </div>

      <!-- Vuetify places each v-tooltip activator as a sibling of the tooltip span; grouping the actions keeps them on one line. -->
      <div class="jobs-top-bar__actions">
        <span v-if="polling" class="jobs-top-bar__poll">
          <v-icon :size="14" class="jobs-top-bar__poll-icon">mdi-clock-outline</v-icon>
          Auto-refreshing while an analysis runs
        </span>

        <density-toggle :value="density" @input="$emit('update:density', $event)" />

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon :loading="loading" aria-label="Refresh jobs" v-bind="attrs" v-on="on" @click="$emit('refresh')">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh now</span>
        </v-tooltip>

        <!-- Disabled buttons swallow hover; the wrapping span carries the "why" tooltip. -->
        <v-tooltip bottom :disabled="!runDisabledReason" max-width="260">
          <template v-slot:activator="{ on, attrs }">
            <span class="jobs-top-bar__run" v-bind="attrs" v-on="on">
              <v-btn color="primary" depressed :disabled="!!runDisabledReason" @click="$emit('run')">
                <v-icon left small>mdi-play</v-icon>
                Run New Analysis
              </v-btn>
            </span>
          </template>
          <span>{{ runDisabledReason }}</span>
        </v-tooltip>
      </div>
    </div>

    <div v-if="runDisabledReason" class="run-guard">
      <v-icon :size="16" class="run-guard__icon">mdi-information</v-icon>
      <span>{{ runDisabledReason }}</span>
    </div>
  </div>
</template>

<script>
  import { JOB_UI_STATUS } from '../../utils/findingsMetrics';
  import { DENSITIES, isDensity } from './densityPreference';
  import DensityToggle from './DensityToggle.vue';

  export default {
    name: 'JobsTopBar',
    components: { DensityToggle },
    props: {
      search: { type: String, default: '' },
      statusFilter: { type: Array, default: () => [] },
      /** { Running: n, Done: n, Failed: n, Cancelled: n } over the unfiltered jobs. */
      statusCounts: { type: Object, default: () => ({}) },
      runDisabledReason: { type: String, default: null },
      density: { type: String, default: DENSITIES.DEFAULT, validator: isDensity },
      loading: { type: Boolean, default: false },
      polling: { type: Boolean, default: false },
    },
    computed: {
      statuses() {
        return Object.values(JOB_UI_STATUS);
      },
    },
    methods: {
      toggleStatus(status) {
        const next = this.statusFilter.includes(status) ? this.statusFilter.filter((value) => value !== status) : [...this.statusFilter, status];
        this.$emit('update:statusFilter', next);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import './aiAnalysisTokens';

  .jobs-top-bar {
    @include ai-analysis-tokens;

    display: flex;
    flex-direction: column;
    gap: var(--ds-space-sm);
    padding: var(--ds-space-md) 0;
    color: var(--aa-text-primary);
  }

  .jobs-top-bar__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--ds-space-md);
  }

  .jobs-top-bar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--ds-space-sm);
    margin-left: auto;
  }

  .theme--dark .jobs-top-bar {
    @include ai-analysis-tokens-dark;
  }

  .jobs-top-bar__search {
    flex: 0 0 320px;
    max-width: 100%;
  }

  .jobs-top-bar__filters {
    display: flex;
    align-items: center;
    gap: var(--ds-space-xs);
    flex-wrap: wrap;
  }

  .jobs-top-bar__poll {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--ds-font-caption);
    color: var(--aa-text-muted);
  }
  .jobs-top-bar__poll-icon { color: inherit !important; }

  .jobs-top-bar__run { display: inline-flex; }
  .jobs-top-bar__run:has(> .v-btn--disabled) { cursor: not-allowed; }

  /* Status filter chips: outlined in the status colour, filled when selected */
  .chip-filter {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 10px;
    font-size: var(--ds-font-caption);
    font-weight: 500;
    color: var(--aa-text-secondary);
    background: var(--aa-surface);
    border: 1px solid var(--aa-border-default);
    border-radius: var(--ds-radius-full);
    cursor: pointer;
    transition: all var(--ds-transition-fast);

    &:hover { background: var(--aa-surface-hover); }
    &:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: 2px; }
  }
  .chip-filter__dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; opacity: 0.85; }
  .chip-filter__count { font-variant-numeric: tabular-nums; opacity: 0.7; }
  .chip-filter[data-status='Running'] { color: var(--aa-status-running); }
  .chip-filter[data-status='Done'] { color: var(--aa-status-done); }
  .chip-filter[data-status='Failed'] { color: var(--aa-status-failed); }
  .chip-filter[data-status='Cancelled'] { color: var(--aa-status-cancelled); }
  .chip-filter[aria-pressed='true'][data-status='Running'] { color: #fff; background: var(--aa-status-running); border-color: transparent; }
  .chip-filter[aria-pressed='true'][data-status='Done'] { color: #fff; background: var(--aa-status-done); border-color: transparent; }
  .chip-filter[aria-pressed='true'][data-status='Failed'] { color: #fff; background: var(--aa-status-failed); border-color: transparent; }
  .chip-filter[aria-pressed='true'][data-status='Cancelled'] { color: #fff; background: var(--aa-status-cancelled); border-color: transparent; }
  .chip-filter[aria-pressed='true'] .chip-filter__count { opacity: 0.85; }

  .run-guard {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 6px 10px;
    font-size: var(--ds-font-caption);
    color: var(--aa-status-stale);
    background: var(--aa-status-stale-bg);
    border-radius: var(--ds-radius-sm);
  }
  .run-guard__icon { color: inherit !important; flex: 0 0 auto; }
</style>
